import React, { useState, useRef } from 'react';
import apiClient from '../api/client';

interface ProcessingResult {
  video_path: string;
  total_frames_processed: number;
  total_detections: number;
  employees_detected: string[];
  sessions_created: number;
  alerts_triggered: number;
  alerts_delivered: number;
  processing_duration_seconds: number;
  errors: string[];
}

export function AdminVideoUpload() {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<ProcessingResult | null>(null);
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    validateAndSetFile(file);
  };

  const validateAndSetFile = (file: File | undefined) => {
    if (file) {
      const ext = file.name.toLowerCase().split('.').pop();
      if (!['mp4', 'avi', 'mov'].includes(ext || '')) {
        setError('Unsupported format. Please upload MP4, AVI, or MOV files.');
        return;
      }
      setSelectedFile(file);
      setError('');
      setResult(null);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    validateAndSetFile(e.dataTransfer.files[0]);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setProgress(0);
    setError('');
    setResult(null);

    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const progressInterval = setInterval(() => {
        setProgress((prev) => Math.min(prev + 5, 90));
      }, 500);

      const response = await apiClient.post('/api/admin/videos/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      clearInterval(progressInterval);
      setProgress(100);
      setResult(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to upload and process video');
    } finally {
      setUploading(false);
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setResult(null);
    setError('');
    setProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDuration = (seconds: number): string => {
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    return `${Math.floor(seconds / 60)}m ${(seconds % 60).toFixed(0)}s`;
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerIcon}>🎬</div>
        <div>
          <h1 style={styles.title}>Video Processing</h1>
          <p style={styles.subtitle}>Upload office footage for posture and wellness analysis</p>
        </div>
      </div>

      {!result && (
        <>
          {/* Upload Zone */}
          <div
            style={{
              ...styles.dropZone,
              ...(isDragging ? styles.dropZoneDragging : {}),
              ...(selectedFile ? styles.dropZoneSelected : {}),
            }}
            onDrop={handleDrop}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".mp4,.avi,.mov"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            
            {selectedFile ? (
              <div style={styles.selectedFile}>
                <div style={styles.fileIconLarge}>🎥</div>
                <div style={styles.fileName}>{selectedFile.name}</div>
                <div style={styles.fileSize}>{formatFileSize(selectedFile.size)}</div>
                <button 
                  style={styles.changeFileBtn}
                  onClick={(e) => { e.stopPropagation(); handleReset(); }}
                >
                  Change file
                </button>
              </div>
            ) : (
              <div style={styles.dropContent}>
                <div style={styles.uploadIconWrapper}>
                  <span style={styles.uploadIcon}>📁</span>
                </div>
                <div style={styles.dropTitle}>Drop your video here</div>
                <div style={styles.dropText}>or click to browse</div>
                <div style={styles.formatBadges}>
                  <span style={styles.formatBadge}>MP4</span>
                  <span style={styles.formatBadge}>AVI</span>
                  <span style={styles.formatBadge}>MOV</span>
                </div>
              </div>
            )}
          </div>

          {/* Error */}
          {error && (
            <div style={styles.errorBox}>
              <span>⚠️</span>
              <span>{error}</span>
            </div>
          )}

          {/* Progress */}
          {uploading && (
            <div style={styles.progressCard}>
              <div style={styles.progressHeader}>
                <span style={styles.progressIcon}>⚙️</span>
                <span style={styles.progressLabel}>
                  {progress < 90 ? 'Uploading video...' : 'Processing frames...'}
                </span>
              </div>
              <div style={styles.progressBar}>
                <div style={{...styles.progressFill, width: `${progress}%`}} />
              </div>
              <div style={styles.progressPercent}>{progress}%</div>
            </div>
          )}

          {/* Actions */}
          {selectedFile && !uploading && (
            <div style={styles.actions}>
              <button onClick={handleReset} style={styles.cancelBtn}>Cancel</button>
              <button onClick={handleUpload} style={styles.uploadBtn}>
                <span>🚀</span>
                <span>Start Processing</span>
              </button>
            </div>
          )}
        </>
      )}

      {/* Results */}
      {result && (
        <div style={styles.resultCard}>
          <div style={styles.resultHeader}>
            <div style={styles.successIcon}>✓</div>
            <div>
              <h2 style={styles.resultTitle}>Processing Complete!</h2>
              <p style={styles.resultPath}>{result.video_path}</p>
            </div>
          </div>

          <div style={styles.resultGrid}>
            <div style={styles.resultMetric}>
              <span style={styles.resultMetricIcon}>🎞️</span>
              <span style={styles.resultMetricValue}>{result.total_frames_processed}</span>
              <span style={styles.resultMetricLabel}>Frames</span>
            </div>
            <div style={styles.resultMetric}>
              <span style={styles.resultMetricIcon}>👁️</span>
              <span style={styles.resultMetricValue}>{result.total_detections}</span>
              <span style={styles.resultMetricLabel}>Detections</span>
            </div>
            <div style={styles.resultMetric}>
              <span style={styles.resultMetricIcon}>👥</span>
              <span style={styles.resultMetricValue}>{result.employees_detected.length}</span>
              <span style={styles.resultMetricLabel}>Employees</span>
            </div>
            <div style={styles.resultMetric}>
              <span style={styles.resultMetricIcon}>📊</span>
              <span style={styles.resultMetricValue}>{result.sessions_created}</span>
              <span style={styles.resultMetricLabel}>Sessions</span>
            </div>
            <div style={styles.resultMetric}>
              <span style={styles.resultMetricIcon}>🔔</span>
              <span style={styles.resultMetricValue}>{result.alerts_triggered}</span>
              <span style={styles.resultMetricLabel}>Alerts</span>
            </div>
            <div style={styles.resultMetric}>
              <span style={styles.resultMetricIcon}>✉️</span>
              <span style={styles.resultMetricValue}>{result.alerts_delivered}</span>
              <span style={styles.resultMetricLabel}>Delivered</span>
            </div>
          </div>

          <div style={styles.resultMeta}>
            <span>⏱️ Processing time: {formatDuration(result.processing_duration_seconds)}</span>
            {result.employees_detected.length > 0 && (
              <span>👤 {result.employees_detected.join(', ')}</span>
            )}
          </div>

          {result.errors.length > 0 && (
            <div style={styles.warningBox}>
              <h4 style={styles.warningTitle}>⚠️ Warnings</h4>
              {result.errors.map((err, idx) => (
                <div key={idx} style={styles.warningItem}>{err}</div>
              ))}
            </div>
          )}

          <button onClick={handleReset} style={styles.newUploadBtn}>
            <span>📁</span>
            <span>Upload Another Video</span>
          </button>
        </div>
      )}

      {/* Info Card */}
      <div style={styles.infoCard}>
        <div style={styles.infoIcon}>💡</div>
        <div>
          <strong>How it works</strong>
          <p style={styles.infoText}>
            Upload office video footage and our AI will analyze posture, detect sedentary behavior, 
            and automatically send wellness reminders to employees via Slack.
          </p>
        </div>
      </div>
    </div>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  container: { maxWidth: '800px', margin: '0 auto' },
  header: { display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px' },
  headerIcon: {
    width: '56px',
    height: '56px',
    borderRadius: '16px',
    background: 'linear-gradient(135deg, #7C3AED 0%, #A78BFA 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '28px',
    boxShadow: '0 8px 24px rgba(124, 58, 237, 0.3)',
  },
  title: { fontSize: '28px', fontWeight: 700, color: '#1E1B4B', margin: 0 },
  subtitle: { fontSize: '15px', color: '#6B7280', marginTop: '4px' },
  dropZone: {
    border: '3px dashed #D1D5DB',
    borderRadius: '24px',
    padding: '60px 40px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    background: 'white',
  },
  dropZoneDragging: {
    borderColor: '#7C3AED',
    background: '#F5F3FF',
  },
  dropZoneSelected: {
    borderColor: '#7C3AED',
    borderStyle: 'solid',
    background: 'linear-gradient(135deg, #F5F3FF 0%, #EDE9FE 100%)',
  },
  dropContent: {},
  uploadIconWrapper: {
    width: '80px',
    height: '80px',
    margin: '0 auto 20px',
    borderRadius: '20px',
    background: 'linear-gradient(135deg, #EDE9FE 0%, #DDD6FE 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  uploadIcon: { fontSize: '36px' },
  dropTitle: { fontSize: '20px', fontWeight: 600, color: '#1E1B4B', marginBottom: '8px' },
  dropText: { fontSize: '15px', color: '#6B7280', marginBottom: '20px' },
  formatBadges: { display: 'flex', justifyContent: 'center', gap: '10px' },
  formatBadge: {
    padding: '6px 14px',
    background: '#F3F4F6',
    borderRadius: '8px',
    fontSize: '12px',
    fontWeight: 600,
    color: '#4B5563',
  },
  selectedFile: {},
  fileIconLarge: { fontSize: '56px', marginBottom: '16px' },
  fileName: { fontSize: '18px', fontWeight: 600, color: '#1E1B4B', marginBottom: '4px' },
  fileSize: { fontSize: '14px', color: '#6B7280', marginBottom: '16px' },
  changeFileBtn: {
    padding: '8px 16px',
    background: 'transparent',
    border: '1px solid #D1D5DB',
    borderRadius: '8px',
    fontSize: '13px',
    color: '#6B7280',
    cursor: 'pointer',
  },
  errorBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '16px 20px',
    background: '#FEE2E2',
    borderRadius: '12px',
    color: '#DC2626',
    marginTop: '20px',
  },
  progressCard: {
    padding: '24px',
    background: 'white',
    borderRadius: '16px',
    marginTop: '24px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
  },
  progressHeader: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' },
  progressIcon: { fontSize: '24px', animation: 'spin 2s linear infinite' },
  progressLabel: { fontSize: '15px', fontWeight: 600, color: '#1E1B4B' },
  progressBar: {
    height: '10px',
    background: '#E5E7EB',
    borderRadius: '5px',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    background: 'linear-gradient(90deg, #7C3AED 0%, #A78BFA 100%)',
    borderRadius: '5px',
    transition: 'width 0.3s ease',
  },
  progressPercent: { textAlign: 'right', fontSize: '13px', color: '#6B7280', marginTop: '8px' },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' },
  cancelBtn: {
    padding: '14px 28px',
    background: '#F3F4F6',
    border: 'none',
    borderRadius: '12px',
    fontSize: '15px',
    fontWeight: 600,
    color: '#4B5563',
    cursor: 'pointer',
  },
  uploadBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '14px 28px',
    background: 'linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)',
    border: 'none',
    borderRadius: '12px',
    fontSize: '15px',
    fontWeight: 600,
    color: 'white',
    cursor: 'pointer',
    boxShadow: '0 8px 20px rgba(124, 58, 237, 0.3)',
  },
  resultCard: {
    padding: '32px',
    background: 'white',
    borderRadius: '24px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
  },
  resultHeader: { display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '28px' },
  successIcon: {
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '28px',
    fontWeight: 700,
    boxShadow: '0 8px 20px rgba(16, 185, 129, 0.3)',
  },
  resultTitle: { fontSize: '22px', fontWeight: 700, color: '#1E1B4B', margin: 0 },
  resultPath: { fontSize: '13px', color: '#6B7280', marginTop: '4px' },
  resultGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '16px',
    marginBottom: '24px',
  },
  resultMetric: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '20px',
    background: '#F9FAFB',
    borderRadius: '16px',
  },
  resultMetricIcon: { fontSize: '24px', marginBottom: '8px' },
  resultMetricValue: { fontSize: '28px', fontWeight: 700, color: '#7C3AED' },
  resultMetricLabel: { fontSize: '12px', color: '#6B7280', marginTop: '4px' },
  resultMeta: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '20px',
    fontSize: '14px',
    color: '#6B7280',
    marginBottom: '20px',
  },
  warningBox: {
    padding: '16px',
    background: '#FFFBEB',
    borderRadius: '12px',
    marginBottom: '20px',
  },
  warningTitle: { fontSize: '14px', fontWeight: 600, color: '#D97706', margin: '0 0 8px' },
  warningItem: { fontSize: '13px', color: '#92400E', marginBottom: '4px' },
  newUploadBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    width: '100%',
    padding: '16px',
    background: 'linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)',
    border: 'none',
    borderRadius: '14px',
    fontSize: '16px',
    fontWeight: 600,
    color: 'white',
    cursor: 'pointer',
    boxShadow: '0 8px 20px rgba(124, 58, 237, 0.3)',
  },
  infoCard: {
    display: 'flex',
    gap: '16px',
    padding: '20px',
    background: '#EFF6FF',
    borderRadius: '16px',
    marginTop: '24px',
  },
  infoIcon: { fontSize: '24px' },
  infoText: { fontSize: '14px', color: '#1E40AF', margin: '8px 0 0', lineHeight: 1.6 },
};
